import { useCheckoutRequestAsync } from "app/hooks/checkout/useCheckoutRequestAsync";
import { useUpdateStandardProductOrder } from "app/hooks/checkout/useUpdateStandardProductOrder";
import { useStandardProductDeliveryCostsCalculator } from "app/hooks/shared/standard-product/useStandardProductDeliveryCostsCalculator";
import { useCartContext } from "app/providers/CartProvider";
import {
  CheckoutTabIds,
  useCheckoutTabsContext,
} from "pages/checkout/providers/CheckoutTabsProvider";
import { toast } from "react-toastify";
import { STORAGE_ORDER_KEY } from "shared/config";
import Button, { ButtonArrowTypes, ButtonTypes } from "shared/ui/button";
import { isTestEnvironment } from "shared/utils/checkEnvironment";
import { concatClassnames } from "shared/utils/concatClassnames";
import { honeyPotToken } from "../info-inputs/components/HiddenInput";

// User ID for confi delete
const userIdDeleted = isTestEnvironment()
  ? "63038104ff54d31e295f255b"
  : "6295d68812f2c57b06b5545d";

// Soft delete payload for the order
const deletePayload = {
  deleted: true,
  selectedReasonDeleted: "webshopchange",
  dateDeleted: "",
  userIdDeleted: userIdDeleted,
};

const CheckoutPanelAppointmentFooter = () => {
  // Get the current tab from the context
  const { updateCurrentTab } = useCheckoutTabsContext();

  // Get the cart context
  const cartContext = useCartContext();
  const cart = cartContext.getCart();

  // Get the checkout request async function and loading state
  const { handleCheckout, isLoading } = useCheckoutRequestAsync();

  // Get the assembly products from the cart
  const { getAssemblyProducts } = useStandardProductDeliveryCostsCalculator(
    cart,
    true,
  );

  // Get the standard order update function and loading state
  const { updateOrder, isUpdateLoading } = useUpdateStandardProductOrder();

  // Get the standard order id
  const orderID = localStorage.getItem(STORAGE_ORDER_KEY);

  // Proceed to the next step
  const onProceedClick = async () => {
    if (cart.withAssembly) {
      cartContext.addCartAssemblyProduct(getAssemblyProducts);
    } else {
      cartContext.removeCartAssemblyProduct(getAssemblyProducts);
    }

    const honeyPot = localStorage.getItem(honeyPotToken);

    try {
      if (!honeyPot) {
        // Check if the order ID is present in local storage
        // If not, proceed with the checkout
        if (!orderID) {
          await handleCheckout();
        }
        // If the order ID is present, delete the order first
        // and then proceed with the checkout
        else {
          // Set the date deleted to the current date
          deletePayload.dateDeleted = new Date().toISOString();

          // Soft delete order first
          await updateOrder.mutateAsync(
            { orderId: orderID, payload: deletePayload },
            {
              // if the order is updated, we need to call the checkout again
              onSuccess: async () => {
                localStorage.removeItem(STORAGE_ORDER_KEY);
                await handleCheckout();
              },
            },
          );
        }
      }
    } catch (error) {
      toast.error(
        "Er is iets misgegaan met uw betaling. Neem voor meer informatie contact op met klantenservice.",
      );
    }
  };

  const canProceed = () => {
    return cart.pickupWarehouse || cart.bumbalFlow;
  };

  const onGoToPrevTabClick = () => {
    updateCurrentTab(CheckoutTabIds.PERSONAL_INFO);
  };

  return (
    <footer
      className={concatClassnames(
        "gg-px-4 sm:gg-px-14 md:gg-px-16 lg:gg-px-14 xl:gg-px-14 2xl:gg-px-32",
        "gg-grid gg-gap-y-5 gg-bg-dark-blue gg-py-11 gg-text-white",
        "gg-items-center sm:gg-flex sm:gg-justify-between sm:gg-gap-x-8",
      )}
    >
      {cart.standardProducts.length > 0 && (
        <Button
          onClick={() => onGoToPrevTabClick()}
          type={ButtonTypes.WHITE}
          arrow={ButtonArrowTypes.LEFT}
          className="gg-flex gg-w-full gg-items-center gg-justify-center gg-justify-self-end gg-whitespace-nowrap gg-px-5 gg-py-3 lg:gg-w-fit lg:gg-max-w-xs xl:gg-py-3.5"
        >
          Terug naar jouw gegevens
        </Button>
      )}
      <Button
        type={ButtonTypes.YELLOW}
        arrow={ButtonArrowTypes.RIGHT}
        onClick={() => onProceedClick()}
        className="gg-flex gg-w-full gg-items-center gg-justify-center gg-justify-self-end gg-px-5 gg-py-3 lg:gg-w-fit lg:gg-px-10 xl:gg-py-3.5"
        disabled={!canProceed()}
        loading={isLoading || isUpdateLoading}
      >
        Bevestigen
      </Button>
    </footer>
  );
};
export default CheckoutPanelAppointmentFooter;
